import React, {ReactNode, RefObject, useEffect, useRef, useState} from 'react'

import IconButton from '../UI/buttons/IconButton.js'
import ListItem from '../UI/surfaces/ListItem.js'
import {surfaceSizes} from '../UI/surfaces/styles.js'
import Popover from '../UI/overlays/Popover.js'
import type {PopoverRef} from '../UI/overlays/Popover.js'
import {useFieldId} from '../UI/forms/utils.js'
import {findTypeahead, moveIndex} from './query.js'
import {searchBar} from './styles.js'

export interface FieldMenuOption {
    value: string
    label: string
    /** Icon class ('mdi mdi-equal') shown before the label */
    icon?: string
    /** Short text shown in the icon's place when there is no icon ('==') */
    symbol?: string
}

export interface FieldMenuProps {
    /** Accessible name and tooltip of the button */
    label: string
    /** mdi icon class of the button, or children */
    icon?: string
    children?: ReactNode
    options: FieldMenuOption[]
    /** The chosen option, marked in the list */
    value?: string
    onSelect: (value: string) => void
    /** The list opens at this element (the field box), like the lists of Select and Input */
    anchorRef: RefObject<HTMLElement>
}

// Typed letters within this time are one search (typeahead)
const typeaheadTimeout = 500

/**
 * An icon button in a field that opens a list to choose from (a select-only combobox): the button keeps the focus,
 * ArrowDown/ArrowUp/Enter/Space open the list, the arrows, Home/End and typing move through it, Enter/Space choose,
 * Escape closes it (overlay stack, so a Modal around it stays open) and Tab closes it and moves on.
 */
export default function FieldMenu({label, icon, children, options, value, onSelect, anchorRef}: FieldMenuProps) {
    const [open, setOpen] = useState(false)
    const [active, setActive] = useState(-1)

    const listId = useFieldId()
    const popoverRef = useRef<PopoverRef>(null)
    const listRef = useRef<HTMLDivElement>(null)

    // Open when the mousedown on the button started, which closes the list (outside click) before the click
    const openOnMouseDown = useRef(false)

    const typeahead = useRef({text: '', timeout: null as ReturnType<typeof setTimeout> | null})
    useEffect(() => () => clearTimeout(typeahead.current.timeout), [])

    // Keeps the keyboard selection in view in a long list (columns)
    useEffect(() => {
        if (open && active >= 0) {
            listRef.current?.querySelector(`#${listId}-${active}`)?.scrollIntoView?.({block: 'nearest'})
        }
    }, [open, active])

    // The keyboard starts at the chosen option or the first one, the mouse only marks the chosen one
    const show = (index?: number, fromMouse?: boolean) => {
        const selected = options.findIndex((option) => option.value === value)
        setActive(index ?? (selected >= 0 || fromMouse ? selected : 0))
        popoverRef.current?.show()
    }

    const close = () => popoverRef.current?.close()

    const select = (index: number) => {
        const option = options[index]
        close()

        if (option) {
            onSelect(option.value)
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (!open) {
            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                show()
            } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                show(options.length - 1)
            }
            return
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            select(active)
            return
        }

        if (event.key === 'Tab') {
            close()
            return
        }

        const next = moveIndex(event.key, active, options.length)
        if (next !== null) {
            event.preventDefault()
            setActive(next)
            return
        }

        // A printable character jumps to the next option starting with what was typed
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const state = typeahead.current
            clearTimeout(state.timeout)
            state.text += event.key
            state.timeout = setTimeout(() => state.text = '', typeaheadTimeout)

            const index = findTypeahead(options.map((option) => option.label), state.text, active)
            if (index >= 0) {
                setActive(index)
            }
        }
    }

    return (
        <>
            <IconButton
                icon={icon}
                // No tooltip while the list is open: it would cover the first option
                tooltip={open ? undefined : label}
                aria-label={label}
                style={{width: searchBar.iconButtonSize, height: searchBar.iconButtonSize, margin: 0}}
                role='combobox'
                aria-haspopup='listbox'
                aria-expanded={open}
                aria-controls={listId}
                aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
                onMouseDown={() => openOnMouseDown.current = open}
                onClick={(event) => {
                    // A click from Enter/Space (detail 0) was handled on keydown already
                    if (event.detail !== 0 && !openOnMouseDown.current) {
                        show(undefined, true)
                    }
                    openOnMouseDown.current = false
                }}
                onKeyDown={onKeyDown}
            >
                {children}
            </IconButton>

            <Popover
                ref={popoverRef}
                anchorRef={anchorRef}
                noClickOpen
                onToggle={setOpen}
                content={
                    <div
                        ref={listRef}
                        id={listId}
                        role='listbox'
                        aria-label={label}
                        style={{maxHeight: searchBar.menuMaxHeight, overflowY: 'auto'}}
                    >
                        {options.map((option, index) => (
                            <div key={option.value} id={`${listId}-${index}`} role='option' aria-selected={option.value === value}>
                                <ListItem
                                    size='sm'
                                    focused={index === active}
                                    active={option.value === value}
                                    icon={option.icon}
                                    // Keeps the focus on the button
                                    onMouseDown={(event) => {
                                        event.preventDefault()
                                        select(index)
                                    }}
                                >
                                    {!option.icon && option.symbol !== undefined &&
                                        <span aria-hidden style={{minWidth: surfaceSizes.listItem.iconSize.sm, marginRight: surfaceSizes.listItem.iconGap}}>{option.symbol}</span>
                                    }
                                    {option.label}
                                </ListItem>
                            </div>
                        ))}
                    </div>
                }
            />
        </>
    )
}
